import React, { useState, useCallback, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchComponent from '../SearchComponent';
import { normalizeReturnRows, sortRowsByDateAscending } from '../../utils/returnStreamRows';

const createPlaceholderRows = () => [{ id: 1, date: 'YYYY-MM-DD', return: '0.0XX' }];

const normalizeRows = (inputRows = []) => {
  const normalized = normalizeReturnRows(inputRows);

  if (!normalized.length) {
    return createPlaceholderRows();
  }

  return normalized;
};

const parseClipboardRows = (clipboardData = '') => (
  normalizeReturnRows(
    clipboardData
      .split(/\r?\n/)
      .map((row, index) => {
        const [date, returnValue] = row.split(/\t/);
        if (!date || !returnValue) {
          return null;
        }
        let cleanedReturnValue = returnValue.trim().replace(/\r$/, '');
        if (cleanedReturnValue.endsWith('%')) {
          cleanedReturnValue = (parseFloat(cleanedReturnValue.slice(0, -1)) / 100).toString();
        }
        return {
          id: index + 1,
          date: date.trim(),
          return: cleanedReturnValue,
        };
      })
      .filter((row) => row),
  )
);

const buildRowsFromEtfSelection = (metadata) => {
  const cumulativeReturns = Array.isArray(metadata?.cumulative_returns) ? metadata.cumulative_returns : [];

  return normalizeReturnRows(
    cumulativeReturns
      .filter((entry) => entry?.date && entry?.return_rate != null)
      .map((entry, index) => ({
        id: index + 1,
        date: String(entry.date).trim(),
        return: String(entry.return_rate).trim(),
      })),
  );
};

const ReturnStreamPaste = ({
  onClear,
  onSubmit,
  initialRows = [],
  initialSelectedEtf = null,
  baseUrl = '',
  searchProviders = null,
}) => {
  const [rows, setRows] = useState(() => normalizeRows(initialRows));
  const [inputMode, setInputMode] = useState('paste');
  const [selectedEtf, setSelectedEtf] = useState(initialSelectedEtf);
  const [loadInfo, setLoadInfo] = useState('');
  const [loadError, setLoadError] = useState('');
  const supportsBenchmarkProviders = Boolean(
    searchProviders?.msci ||
    searchProviders?.sp ||
    searchProviders?.iceBofa ||
    searchProviders?.famaFrench ||
    searchProviders?.ftseRussell,
  );

  useEffect(() => {
    setRows(normalizeRows(initialRows));
  }, [initialRows]);

  useEffect(() => {
    setSelectedEtf(initialSelectedEtf);
  }, [initialSelectedEtf]);

  const columns = [
    { field: 'date', headerName: 'Date', width: 150, editable: true },
    { field: 'return', headerName: 'Monthly Return', width: 150, editable: true },
  ];

  const getSubmittableRows = (candidateRows = []) => (
    sortRowsByDateAscending(
      candidateRows.filter(
        (row) =>
          row.date &&
          row.date !== 'YYYY-MM-DD' &&
          row.return !== undefined &&
          row.return !== null &&
          String(row.return).trim() !== '',
      ),
    )
  );

  const handleClear = () => {
    setRows(createPlaceholderRows());
    setSelectedEtf(null);
    setLoadInfo('');
    setLoadError('');
    onClear();
  };

  const handlePaste = useCallback(async (event) => {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text')
      || await navigator.clipboard.readText().catch(() => '');
    const parsedRows = parseClipboardRows(clipboardData);

    if (!parsedRows.length) {
      setLoadInfo('');
      setLoadError('No valid date/return rows were detected in pasted data.');
      return;
    }

    setRows(parsedRows);
    setSelectedEtf(null);
    setLoadInfo(`Loaded ${parsedRows.length} monthly return rows from paste.`);
    setLoadError('');
    onSubmit(parsedRows, null);
  }, [onSubmit]);

  const handleSelectEtf = (_, metadata) => {
    const mappedRows = buildRowsFromEtfSelection(metadata);
    if (!mappedRows.length) {
      setLoadInfo('');
      setLoadError('Selected symbol did not return monthly data. Please choose another symbol.');
      return;
    }

    setRows(mappedRows);
    setSelectedEtf(metadata || null);
    setLoadInfo(
      `Loaded ${mappedRows.length} monthly return rows from ${metadata?.symbol || 'selected symbol'}.`,
    );
    setLoadError('');
    onSubmit(mappedRows, metadata || null);
  };

  const processRowUpdate = (updatedRow) => {
    const normalizedRow = {
      ...updatedRow,
      date: String(updatedRow.date || '').trim(),
      return: String(updatedRow.return || '').trim(),
    };

    const nextRows = sortRowsByDateAscending(
      rows.map((row) => (
        row.id === updatedRow.id
          ? normalizedRow
          : row
      )),
    );
    setRows(nextRows);
    onSubmit(getSubmittableRows(nextRows), selectedEtf);

    return normalizedRow;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Fund Return Input Method
      </Typography>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={inputMode}
          exclusive
          fullWidth
          size="medium"
          color="primary"
          onChange={(_, nextMode) => {
            if (nextMode) {
              setInputMode(nextMode);
              setLoadError('');
            }
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
            },
          }}
        >
          <ToggleButton value="paste">Paste</ToggleButton>
          <ToggleButton value="search">{supportsBenchmarkProviders ? 'Search Source' : 'Search Symbol'}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {selectedEtf?.symbol && inputMode !== 'search' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {`Selected Symbol: ${selectedEtf.symbol}${selectedEtf.name ? ` - ${selectedEtf.name}` : ''}`}
        </Alert>
      )}

      {loadInfo && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {loadInfo}
        </Alert>
      )}
      {loadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}

      {inputMode === 'search' ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <SearchComponent
            onSelect={handleSelectEtf}
            baseUrl={baseUrl}
            initialSelectedEtf={selectedEtf}
            showPreviewChart={false}
            searchProviders={searchProviders}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }} onPaste={handlePaste}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Paste tab-separated date and monthly return values directly into the table.
          </Typography>
          <DataGrid
            rows={rows}
            columns={columns}
            processRowUpdate={processRowUpdate}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            pageSizeOptions={[5]}
            hideFooter
            autoHeight={false}
            sx={{
              height: 300,
              '& .MuiDataGrid-footerContainer': { display: 'none' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

ReturnStreamPaste.propTypes = {
  onClear: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialRows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    return: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })),
  initialSelectedEtf: PropTypes.shape({
    symbol: PropTypes.string,
    name: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    cumulative_returns: PropTypes.array,
  }),
  baseUrl: PropTypes.string,
  searchProviders: PropTypes.shape({
    yahoo: PropTypes.bool,
    msci: PropTypes.bool,
    sp: PropTypes.bool,
    iceBofa: PropTypes.bool,
    famaFrench: PropTypes.bool,
    ftseRussell: PropTypes.bool,
  }),
};

export default ReturnStreamPaste;
